/**
 * The record a rejection is about, when the API named one — the active record that
 * already holds the number a form typed (TRKC-83). `kind` is the owner kind the API
 * routes records by (Broker, Carrier…); the portal turns it into a link.
 */
export interface ErrorSubject {
  kind: string
  id: string
  name: string
  /**
   * The record this one lives under, when it has no page of its own — a truck,
   * trailer or driver is reached through its carrier (Stage 2, 2.1).
   */
  ownerId?: string | null
}

/** RFC 7807 payload returned by the API for handled errors. */
interface ProblemDetails {
  title?: string
  detail?: string
  status?: number
  /** Stable identifier for a rejection the user can act on (DomainErrorCodes). */
  code?: string
  /** Every broken rule, when the API validated a whole submission at once. */
  errors?: { code: string; message: string; subject?: ErrorSubject | null }[]
}

export class ApiError extends Error {
  readonly status: number

  /** The ProblemDetails title — the machine-readable half of the error contract. */
  readonly title: string | null

  /**
   * The domain error code, when the API sent one. `message` is the API's English
   * sentence and is never shown to a user; this is what the portal translates.
   */
  readonly code: string | null

  /**
   * Every rule the submission broke, in the order the domain checked them. One bad field
   * yields one entry; a form with three mistakes yields three, so the user fixes them in
   * one pass instead of one save per problem.
   */
  readonly codes: readonly string[]

  /** The record each code is about, when the API named one — keyed by code. */
  readonly subjects: Readonly<Record<string, ErrorSubject>>

  /**
   * Every broken rule with its own record, in order — for a refusal where one code names
   * several records (a broadcast refusing three agents who opted out), which `subjects`,
   * keyed by code, can only keep one of.
   */
  readonly problems: readonly { code: string; subject: ErrorSubject | null }[]

  constructor(
    message: string,
    status: number,
    title: string | null = null,
    code: string | null = null,
    codes: readonly string[] = [],
    subjects: Readonly<Record<string, ErrorSubject>> = {},
    problems: readonly { code: string; subject: ErrorSubject | null }[] = [],
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.title = title
    this.code = code
    this.codes = codes.length > 0 ? codes : code === null ? [] : [code]
    this.subjects = subjects
    this.problems =
      problems.length > 0 ? problems : this.codes.map((each) => ({ code: each, subject: subjects[each] ?? null }))
  }
}

async function request<TResponse>(path: string, init: RequestInit = {}): Promise<TResponse> {
  // Headers are merged, never replaced: callers add their own (an Authorization
  // header, say) without silently dropping the ones set here.
  const headers = new Headers(init.headers)

  // Only describe a body that exists. Sending Content-Type on a GET is meaningless and
  // would turn a simple cross-origin request into a preflighted one. FormData is
  // excepted: the browser must write that header itself to include the multipart
  // boundary, and a hand-set value would break the upload.
  const hasBody = init.body !== undefined && init.body !== null
  if (hasBody && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(path, { ...init, headers })

  if (!response.ok) {
    throw await readError(response)
  }

  // 204 has no body by definition; parsing it would throw.
  if (response.status === 204) {
    return undefined as TResponse
  }

  return (await response.json()) as TResponse
}

async function readError(response: Response): Promise<ApiError> {
  try {
    const problem = (await response.json()) as ProblemDetails
    const subjects: Record<string, ErrorSubject> = {}

    for (const error of problem.errors ?? []) {
      if (error.subject !== undefined && error.subject !== null) {
        subjects[error.code] = error.subject
      }
    }

    return new ApiError(
      problem.detail ?? problem.title ?? `Request failed (${response.status})`,
      response.status,
      problem.title ?? null,
      problem.code ?? null,
      problem.errors?.map((error) => error.code) ?? [],
      subjects,
      problem.errors?.map((error) => ({ code: error.code, subject: error.subject ?? null })) ?? [],
    )
  } catch {
    return new ApiError(`Request failed (${response.status})`, response.status)
  }
}

export function apiPost<TResponse>(
  path: string,
  body: unknown,
  init?: RequestInit,
): Promise<TResponse> {
  return request<TResponse>(path, { ...init, method: 'POST', body: JSON.stringify(body) })
}

export function apiPut<TResponse>(
  path: string,
  body: unknown,
  init?: RequestInit,
): Promise<TResponse> {
  return request<TResponse>(path, { ...init, method: 'PUT', body: JSON.stringify(body) })
}

export function apiGet<TResponse>(path: string, init?: RequestInit): Promise<TResponse> {
  return request<TResponse>(path, { ...init, method: 'GET' })
}

/** POST with a multipart body — the browser sets the content type and its boundary. */
export function apiUpload<TResponse>(
  path: string,
  form: FormData,
  init?: RequestInit,
): Promise<TResponse> {
  return request<TResponse>(path, { ...init, method: 'POST', body: form })
}

/**
 * Fetches a file the API guards with the bearer token — a plain link cannot carry it,
 * so the bytes come back as a Blob for the caller to hand to the browser. The name is
 * deliberately NOT parsed out of Content-Disposition: the header carries both the
 * plain and the RFC 5987 spellings and parsing them correctly is a bug farm — every
 * caller already knows the name from the row it clicked.
 */
export async function apiDownload(path: string, init: RequestInit = {}): Promise<Blob> {
  const headers = new Headers(init.headers)
  const response = await fetch(path, { ...init, method: 'GET', headers })

  if (!response.ok) {
    throw await readError(response)
  }

  return response.blob()
}
